import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTabManager, useTabParams } from '../../tab-manager';
import { Tooltip, Icon } from '@cotera/client/app/components/ui';
import { IconName } from '@cotera/client/app/components/ui';
import { CloseTabButton } from './close-tab';

const ITEM_TYPE = 'TAB';

interface DragItem {
  id: string;
  index: number;
}

interface TabItemProps {
  tab: {
    id: string;
    title: string;
    displayTitle?: string;
    icon?: string;
    tabView?: React.ReactNode;
    showClose?: boolean;
    onClose?: (tabId: string) => boolean;
  };
}

export const TabItem: React.FC<TabItemProps> = ({ tab }) => {
  const {
    navigate,
    reorderTabs,
    renderedTabs: tabs,
    activeTab,
  } = useTabManager();
  const { order } = useTabParams<{ order: number }>();
  const tabArr = Object.values(tabs);

  // Use provided data or fallback to title
  const displayTitle = tab.displayTitle ?? tab.title;
  const tabIcon = tab.icon as IconName | undefined;
  const moveTab = (dragIndex: number, hoverIndex: number) => {
    const draggedTab = tabArr[dragIndex]!;
    const newTabOrder = [...tabArr];

    // Remove the dragged item
    newTabOrder.splice(dragIndex, 1);
    // Insert it at the new position
    newTabOrder.splice(hoverIndex, 0, draggedTab);

    reorderTabs(newTabOrder.map((tab) => tab.id));
  };

  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { id: tab.id, index: order },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (draggedItem: DragItem) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = order;

      if (dragIndex === hoverIndex) {
        return;
      }

      moveTab(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  drag(drop(ref));

  return (
    <div
      key={tab.id}
      ref={ref}
      className={`
          gap-x-1
          border-l border-border last:border-r
          h-full flex justify-between items-center pl-4 pr-1 cursor-pointer
          max-w-[128px] group relative transition-all
          ${isDragging ? 'opacity-50' : 'opacity-100'}
          ${isOver ? 'bg-blue-100' : ''}
          ${
            tab.id === activeTab
              ? 'text-foreground bg-background border-b border-b-primary/60'
              : 'hover:bg-gray-100 text-muted-foreground bg-muted-background'
          }
        `}
      onClick={() => navigate(tab.id)}
    >
      <Tooltip
        side="bottom"
        tooltipContent={<p>{tab.title}</p>}
        asChild
        openDelay={150}
        closeDelay={50}
      >
        <div className="flex items-center gap-2 truncate">
          {tabIcon && <Icon icon={tabIcon} size="small" className="shrink-0" />}
          <span className="truncate ellipsis text-sm font-medium">
            {displayTitle}
          </span>
        </div>
      </Tooltip>
      {tab.tabView !== undefined && tab.tabView}
      {(tab.showClose ?? true) && (
        <CloseTabButton tabId={tab.id} onClose={tab.onClose} />
      )}
    </div>
  );
};
